/**
 * Hover motion for the rail icons (RailIcons.tsx), on the Web Animations API.
 *
 * Hovering plays a part's animation forward; leaving plays it backward from
 * wherever it has got to, so a quick pass over an item unwinds instead of
 * finishing and then snapping. A forward run that completes holds its last
 * frame while the pointer stays (the gear rests turned). A run that reaches
 * rest is cancelled, handing the part back to its own styles. Every run
 * starts and ends on a frame the part already shows, so neither end jumps.
 *
 * Motion travels on eased in-out segments, and on the way in overshoots a
 * touch before it settles; the keyframes carry their own easing, so
 * `options` sets none. The way home has no overshoot. Played backward, the
 * overshoot would come first, as a wind-up. So a part that overshoots names
 * `leaveKeyframes`, the same move without it, to play once it has settled.
 *
 * @module components/icons/railIconMotion
 */

export interface RailMotion {
  readonly keyframes: Keyframe[];
  /** From the settled last frame back to rest, played forward in place of reversing. */
  readonly leaveKeyframes?: Keyframe[];
  readonly options: KeyframeAnimationOptions;
}

/** The travel of every move: eased in, eased out. */
const TRAVEL = "cubic-bezier(0.65, 0, 0.35, 1)";
/** The gentler settle out of an overshoot. */
const SETTLE = "cubic-bezier(0.37, 0, 0.63, 1)";

/** Named in the markup as `data-rail-motion="<name>"`. */
export const RAIL_MOTIONS = {
  "chat-front": {
    keyframes: [
      { transform: "translate(0, 0)", easing: TRAVEL },
      { transform: "translate(-1px, 1px)", offset: 0.4, easing: TRAVEL },
      { transform: "translate(0.3px, -0.3px)", offset: 0.75, easing: SETTLE },
      { transform: "translate(0, 0)" },
    ],
    leaveKeyframes: [
      { transform: "translate(0, 0)", easing: TRAVEL },
      { transform: "translate(-1px, 1px)", offset: 0.6, easing: TRAVEL },
      { transform: "translate(0, 0)" },
    ],
    options: { duration: 650 },
  },
  "chat-back": {
    keyframes: [
      { transform: "translate(0, 0)", easing: TRAVEL },
      { transform: "translate(1px, -1px)", offset: 0.4, easing: TRAVEL },
      { transform: "translate(-0.3px, 0.3px)", offset: 0.75, easing: SETTLE },
      { transform: "translate(0, 0)" },
    ],
    leaveKeyframes: [
      { transform: "translate(0, 0)", easing: TRAVEL },
      { transform: "translate(1px, -1px)", offset: 0.6, easing: TRAVEL },
      { transform: "translate(0, 0)" },
    ],
    options: { duration: 650 },
  },
  "roof-lift": {
    keyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(-1.25px)", offset: 0.35, easing: TRAVEL },
      { transform: "translateY(0.35px)", offset: 0.72, easing: SETTLE },
      { transform: "translateY(0)" },
    ],
    leaveKeyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(-1.25px)", offset: 0.65, easing: TRAVEL },
      { transform: "translateY(0)" },
    ],
    options: { duration: 600 },
  },
  // roof-lift's quieter twin: a sheet under a lifting one follows it halfway.
  "layer-lift": {
    keyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(-0.6px)", offset: 0.35, easing: TRAVEL },
      { transform: "translateY(0.15px)", offset: 0.72, easing: SETTLE },
      { transform: "translateY(0)" },
    ],
    leaveKeyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(-0.6px)", offset: 0.65, easing: TRAVEL },
      { transform: "translateY(0)" },
    ],
    options: { duration: 600 },
  },
  // The column sinks into its base and rises again. Drawn on `pathLength=1`
  // from the base up, so a dash offset of 1 is an empty column. No overshoot:
  // a column cannot grow past its own length.
  "column-rise": {
    keyframes: [
      { strokeDasharray: "1", strokeDashoffset: "0", easing: "ease-in" },
      { strokeDasharray: "1", strokeDashoffset: "1", offset: 0.3, easing: TRAVEL },
      { strokeDasharray: "1", strokeDashoffset: "0" },
    ],
    options: { duration: 600 },
  },
  "inbox-catch": {
    keyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(1.5px)", offset: 0.45, easing: TRAVEL },
      { transform: "translateY(-0.4px)", offset: 0.75, easing: SETTLE },
      { transform: "translateY(0)" },
    ],
    leaveKeyframes: [
      { transform: "translateY(0)", easing: TRAVEL },
      { transform: "translateY(1.5px)", offset: 0.55, easing: TRAVEL },
      { transform: "translateY(0)" },
    ],
    options: { duration: 550 },
  },
  // Invisible at rest (`opacity-0` in the markup) and at both ends, so it can
  // play either way without popping in or out.
  "inbox-drop": {
    keyframes: [
      { opacity: 0, transform: "translateY(-6px)" },
      { opacity: 1, offset: 0.15 },
      { opacity: 1, transform: "translateY(0)", offset: 0.55 },
      { opacity: 0, transform: "translateY(2px)" },
    ],
    options: { duration: 550, easing: "ease-in" },
  },
  // A gauge's needle sweeps back across the dial and settles home, as if re-reading the level.
  "needle-sweep": {
    keyframes: [
      { transform: "rotate(0deg)", easing: TRAVEL },
      { transform: "rotate(-75deg)", offset: 0.45, easing: TRAVEL },
      { transform: "rotate(6deg)", offset: 0.78, easing: SETTLE },
      { transform: "rotate(0deg)" },
    ],
    leaveKeyframes: [
      { transform: "rotate(0deg)", easing: TRAVEL },
      { transform: "rotate(-75deg)", offset: 0.55, easing: TRAVEL },
      { transform: "rotate(0deg)" },
    ],
    options: { duration: 700 },
  },
  // Turns a notch past where it stops and settles back.
  "gear-turn": {
    keyframes: [
      { transform: "rotate(0deg)", easing: TRAVEL },
      { transform: "rotate(98deg)", offset: 0.7, easing: SETTLE },
      { transform: "rotate(90deg)" },
    ],
    leaveKeyframes: [{ transform: "rotate(90deg)", easing: TRAVEL }, { transform: "rotate(0deg)" }],
    options: { duration: 500 },
  },
} satisfies Record<string, RailMotion>;

export type RailMotionName = keyof typeof RAIL_MOTIONS;

export function isRailMotionName(name: string | undefined): name is RailMotionName {
  return name !== undefined && Object.hasOwn(RAIL_MOTIONS, name);
}

/** A part's run without its delay, in milliseconds. */
export function motionDuration(motion: RailMotion): number {
  return typeof motion.options.duration === "number" ? motion.options.duration : 0;
}

export interface RailMotionPlayer {
  /** Head for the hovered state, picking up a run home where it is. */
  readonly enter: () => void;
  /** Head home from the current point. */
  readonly leave: () => void;
  /** Drop the animation and return the part to rest at once. */
  readonly stop: () => void;
}

/**
 * One part's player. `delay` staggers a part within its icon, and `span` is
 * the length of the icon's whole run: whatever the part leaves of it becomes
 * its end delay, so every part runs the same total length and the way home
 * is the way out mirrored — the last part to start is the first to unwind.
 */
export function createRailMotionPlayer(
  element: Pick<Element, "animate">,
  motion: RailMotion,
  { delay = 0, span = 0 }: { delay?: number; span?: number } = {},
): RailMotionPlayer {
  const endDelay = Math.max(0, span - delay - motionDuration(motion));
  let animation: Animation | null = null;
  // Whether `animation` plays `leaveKeyframes`, whose forward direction is home.
  let leaving = false;

  const play = (keyframes: Keyframe[], timing: { delay: number; endDelay: number }) => {
    const next = element.animate(keyframes, { ...motion.options, ...timing, fill: "both" });
    const isLeave = keyframes === motion.leaveKeyframes;
    next.onfinish = () => {
      const reachedRest = isLeave ? next.playbackRate > 0 : next.playbackRate < 0;
      if (!reachedRest) return;
      next.cancel();
      if (animation === next) animation = null;
    };
    animation = next;
    leaving = isLeave;
  };
  const headingHome = (current: Animation) =>
    leaving ? current.playbackRate > 0 : current.playbackRate < 0;

  return {
    enter() {
      if (!animation) play(motion.keyframes, { delay, endDelay });
      else if (headingHome(animation)) animation.reverse();
    },
    leave() {
      if (!animation || headingHome(animation)) return;
      // Settled in the hovered pose: take the way home without the overshoot,
      // its timing mirrored so staggered parts unwind last-in, first-out.
      if (!leaving && motion.leaveKeyframes && animation.playState === "finished") {
        animation.cancel();
        play(motion.leaveKeyframes, { delay: endDelay, endDelay: delay });
        return;
      }
      animation.reverse();
    },
    stop() {
      animation?.cancel();
      animation = null;
    },
  };
}
